import React from 'react';

/**
 * TabShell (Aurora Cockpit) — the ONE padding + section-gap wrapper for tab
 * surfaces (`.da-tab`; see src/styles/auroraCockpit.css). Ends the
 * p-4 / p-5 / p-6 / p-8 padding chaos: a single clamped `--tab-pad-x/-y`, a
 * uniform `--tab-section-gap` between children, and a readable `--tab-max`
 * content cap, centred.
 *
 * Use as the outermost element of a tab's content. Children are stacked with
 * the canonical inter-section gap, so a tab is just:
 *   <TabShell><SiteTabSectionHeader .../><section className="da-panel">…</section></TabShell>
 *
 * `bleed` switches to the full-bleed `.da-tab--bleed` variant for map / canvas
 * tabs that own their own padding.
 *
 * Surfaces are NOT migrated onto this yet (FOUNDATION wave) — it's provided so
 * the surface wave has a single, typed shell to adopt.
 */
export interface TabShellProps {
  children: React.ReactNode;
  /** Full-bleed (no max-width, no padding, no gap) — for map/canvas tabs. */
  bleed?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const TabShell: React.FC<TabShellProps> = ({
  children,
  bleed = false,
  className = '',
  'data-testid': testId,
}) => (
  <div
    className={`da-tab${bleed ? ' da-tab--bleed' : ''} ${className}`.trim()}
    data-testid={testId}
  >
    {children}
  </div>
);
